import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as R from 'ramda';
import { func, number, oneOfType, shape, string } from 'prop-types';
import { renderInput } from '../../utils/renderUtils';
import { useGetDestinationsQuery, useGetStatesQuery } from '../../customerAuthorizationsEndpoints';
import SearchResult from './SearchResult';

const emptyState = { id: '', name: '' };

export const renderTags = (tags, getTagProps) =>
  tags.map((item, index) => (
    <Chip
      label={item.destinationNameShort}
      sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
      variant="outlined"
      {...getTagProps({ index })}
    />
  ));

const Destination = ({ info, idName, setExpanded, setInfo }) => {
  const { destinations = [] } = info;

  const dataCompanyId = info?.dataCompanyId;

  const [destinationName, setDestinationName] = useState('');
  const [destinationState, setDestinationState] = useState(emptyState);

  const { data: statesQuery = [], isFetching: isFetchingStates } = useGetStatesQuery();

  const params = {
    dataCompanyId,
    destinationCity: '',
    destinationName,
    destinationState: destinationState.name,
  };

  const disabled = destinationName.length < 1 || R.equals(destinationState, emptyState);

  const { destinationsQuery = [], isFetching: isFetchingDestinations } = useGetDestinationsQuery(
    { params },
    {
      selectFromResult: ({ data }) => ({
        destinationsQuery: data?.filter(
          destination =>
            !destinations.find(location => destination.destinationIdName === location.locationId)
        ),
      }),
      skip: disabled,
    }
  );

  const handleChange = (_, value, reason) => {
    if (reason === 'selectOption') {
      setDestinationState(value);
    }
    if (reason === 'clear') {
      setDestinationState(emptyState);
    }
  };

  return (
    <>
      <Box sx={{ display: 'grid', gridGap: 16, gridTemplateColumns: '1fr', mt: 2 }}>
        <Typography variant="caption">
          You must select a state and enter a Destination Name to see a list of destinations
        </Typography>
        <Box
          sx={{
            display: 'flex',
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            flexDirection: { xs: 'column', lg: 'row' },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            gridColumnGap: { xs: 0, lg: 8 },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            gridRowGap: { xs: 8, lg: 0 },
          }}
        >
          <Autocomplete
            fullWidth
            getOptionLabel={R.prop('name')}
            isOptionEqualToValue={R.eqProps('id')}
            loading={isFetchingStates}
            onChange={handleChange}
            options={R.prepend(emptyState, statesQuery)}
            renderInput={renderInput({
              label: 'State',
              length: R.length(destinationsQuery),
              loading: isFetchingStates,
              required: true,
            })}
            renderTags={renderTags}
            value={destinationState}
          />
          <TextField
            fullWidth
            label="Destination Name"
            name="destinationName"
            onChange={e => setDestinationName(e.target.value)}
            required
            value={destinationName}
            variant="outlined"
          />
        </Box>
      </Box>
      {R.all(R.equals(R.T()), [
        R.not(R.isEmpty(destinationName)),
        R.not(R.isEmpty(R.prop('name', destinationState))),
        R.not(isFetchingDestinations),
      ]) && (
        <Stack mt={2} spacing={2}>
          <Typography>
            {destinationsQuery.length} Results for &#34;{destinationName}&#34; in{' '}
            {destinationState.name}
          </Typography>
          {destinationsQuery.map(destination => (
            <SearchResult
              destination={destination}
              idName={idName}
              info={info}
              key={destination.destinationIdName}
              setDestinationName={setDestinationName}
              setDestinationState={setDestinationState}
              setExpanded={setExpanded}
              setInfo={setInfo}
            />
          ))}
        </Stack>
      )}
    </>
  );
};

Destination.propTypes = {
  idName: oneOfType([string, number]).isRequired,
  info: shape({}).isRequired,
  setExpanded: func.isRequired,
  setInfo: func.isRequired,
};

export default Destination;
